'use client';

import { ArrowRight, ClipboardList, MessageCircle } from 'lucide-react';
import { motion, useReducedMotion } from 'motion/react';
import { useTranslations } from 'next-intl';

import { UnifiedContainer } from '@/components/layout/UnifiedContainer';
import { ViberLink } from '@/components/ui/viber-link';
import { WhatsAppBrandMark } from '@/components/ui/whatsapp-icon';
import { WhatsAppLink } from '@/components/ui/whatsapp-link';
import { Link } from '@/i18n/routing';
import { cn } from '@/lib/utils';

export function SalonBeautyCleaningBookingPath() {
  const t = useTranslations('servicesPage.salonBeautyCleaningLanding.path');
  const tFooter = useTranslations('common.footer');
  const tReveal = useTranslations('common.phoneReveal');
  const reducedMotion = useReducedMotion();

  return (
    <section
      id='quote'
      className='relative scroll-mt-24 border-b border-oxford-blue/8 bg-linen py-12 sm:py-16'
    >
      <UnifiedContainer size='xl' padding='lg'>
        <motion.div
          className='mx-auto max-w-3xl text-left sm:text-center'
          initial={reducedMotion ? false : { opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
        >
          <h2 className='text-2xl font-extrabold tracking-tight text-oxford-blue sm:text-3xl md:text-4xl'>
            {t('title')}
          </h2>
          <p className='mt-3 text-base leading-relaxed text-oxford-blue/70 sm:text-lg'>
            {t('subtitle')}
          </p>
        </motion.div>

        <div className='mx-auto mt-8 grid max-w-4xl gap-4 md:grid-cols-2 md:gap-6'>
          <motion.div
            initial={reducedMotion ? false : { opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.35 }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            className={cn(
              'flex h-full flex-col rounded-2xl border p-5 sm:p-6',
              'border-orange-peel/30 bg-white shadow-[0_18px_40px_-24px_rgba(249,115,22,0.45)]',
            )}
          >
            <div className='mb-4 flex items-center gap-3'>
              <span
                className='flex h-11 w-11 items-center justify-center rounded-xl bg-orange-peel/12 text-orange-peel'
                aria-hidden
              >
                <MessageCircle className='h-5 w-5' strokeWidth={2.25} />
              </span>
              <h3 className='text-lg font-bold text-oxford-blue sm:text-xl'>{t('quote.title')}</h3>
            </div>
            <p className='flex-1 text-sm leading-relaxed text-oxford-blue/70 sm:text-base'>
              {t('quote.body')}
            </p>
            <div className='mt-5 flex flex-col gap-2 sm:flex-row sm:flex-wrap'>
              <WhatsAppLink
                className='inline-flex items-center gap-2 text-sm font-bold text-orange-peel underline-offset-4 hover:underline'
                ariaLabel={t('quote.aria')}
              >
                <WhatsAppBrandMark className='h-4 w-4 rounded-sm' />
                {t('quote.cta')}
                <ArrowRight className='h-4 w-4' aria-hidden />
              </WhatsAppLink>
              <ViberLink
                className='inline-flex items-center gap-2 text-sm font-bold text-oxford-blue underline-offset-4 hover:underline'
                ariaLabel={tReveal('viberLabel')}
              >
                {tFooter('viber')}
                <ArrowRight className='h-4 w-4' aria-hidden />
              </ViberLink>
            </div>
          </motion.div>

          <motion.div
            initial={reducedMotion ? false : { opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.35 }}
            transition={{
              duration: 0.45,
              delay: reducedMotion ? 0 : 0.08,
              ease: [0.16, 1, 0.3, 1],
            }}
          >
            <Link
              href='/contact'
              className={cn(
                'group flex h-full flex-col rounded-2xl border p-5 transition-colors sm:p-6',
                'border-oxford-blue/12 bg-white hover:border-oxford-blue/25',
              )}
            >
              <div className='mb-4 flex items-center gap-3'>
                <span
                  className='flex h-11 w-11 items-center justify-center rounded-xl bg-oxford-blue/8 text-oxford-blue'
                  aria-hidden
                >
                  <ClipboardList className='h-5 w-5' strokeWidth={2.25} />
                </span>
                <h3 className='text-lg font-bold text-oxford-blue sm:text-xl'>
                  {t('details.title')}
                </h3>
              </div>
              <p className='flex-1 text-sm leading-relaxed text-oxford-blue/70 sm:text-base'>
                {t('details.body')}
              </p>
              <span className='mt-5 inline-flex items-center gap-2 text-sm font-bold text-oxford-blue'>
                {t('details.cta')}
                <ArrowRight
                  className='h-4 w-4 transition-transform group-hover:translate-x-0.5'
                  aria-hidden
                />
              </span>
            </Link>
          </motion.div>
        </div>
      </UnifiedContainer>
    </section>
  );
}
